/**
 * Container for the Exclude action: renders the trigger and owns what `ExcludeModal` does not — the
 * entitlement gate, the `api` lookup, the "Logged as" snapshot, and the submit state machine.
 */
import { Box, Button, Tooltip } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import { useCodingScreen } from '../../interface/context';
import type {
  ExcludeActionData,
  ExcludeDictionaryEntry,
  ExcludePayload
} from '../../interface/exclude';
import type { SlotProps } from '../../interface/slotProps';
import { readState } from '../../slots/readState';
import {
  EXCLUDE_COLORS,
  EXCLUDE_GLYPH_PX,
  EXCLUDE_SIZES,
  CASE_ACTION_RADIUS
} from '../../styles/tokens';
import {
  EXCLUDE_MODAL_TITLE,
  ExcludeGlyph,
  ExcludeModal,
  type ExcludeSubmitState
} from '../ExcludeModal';

/** Narrow the slot `value` (unknown) to `ExcludeActionData`: only a genuine non-null, non-array object;
 *  anything else becomes `{}` so a malformed payload degrades to "not entitled" rather than throwing. The
 *  degradation is deliberately SILENT (no `console.error`) -- the same choice `PatientInfo`'s value guard
 *  makes, and the shell's zero-unexpected-console-error assertions depend on it. */
const asActionData = (value: unknown): ExcludeActionData =>
  value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as ExcludeActionData)
    : {};

/** Keep only well-formed dictionary rows; a malformed vocabulary becomes empty rather than rendering
 *  `undefined` labels or throwing mid-render. */
const asOptions = (
  value: unknown
): readonly ExcludeDictionaryEntry[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const wellFormed = value.filter(
    (entry): entry is ExcludeDictionaryEntry =>
      entry != null &&
      typeof entry === 'object' &&
      // A non-empty code: `''` is a `string` and would otherwise survive every guard, reach the payload as
      // `otherCodes: ['']`, and render a pill with an empty React key.
      typeof (entry as ExcludeDictionaryEntry).code === 'string' &&
      (entry as ExcludeDictionaryEntry).code.trim().length > 0 &&
      typeof (entry as ExcludeDictionaryEntry).label === 'string'
  );
  // De-duplicate by `code`, first-seen wins. A dictionary that repeats a code would otherwise render two
  // pills sharing a React key and let the same code reach the payload twice.
  return wellFormed.filter(
    (entry, i) => wellFormed.findIndex((e) => e.code === entry.code) === i
  );
};

const nameOf = (data: ExcludeActionData): string =>
  typeof data.loggedAs?.displayName === 'string' &&
  data.loggedAs.displayName.trim().length > 0
    ? data.loggedAs.displayName
    : 'XXX'; // placeholder until a real current user is injected (same gap Hold still carries)

export const ExcludeAction: React.FC<SlotProps> = ({ value }) => {
  const { api, state } = useCodingScreen();
  const data = asActionData(value);

  // Latches below are per-case. `CodingScreenShell` mounts without a case `key`, so same-route case
  // navigation reuses this instance; without a reset a confirmed exclusion on one case locks the next.
  // `case.identifier` is unset when the mapper has no `case_id`, leaving such cases indistinguishable
  // here — the complete fix is a case-keyed mount upstream.
  const caseKey = readState(state, 'case.identifier');

  const [isOpen, setIsOpen] = useState(false);
  const [loggedAs, setLoggedAs] = useState<{ name: string; at: Date } | null>(
    null
  );
  const [submitState, setSubmitState] = useState<ExcludeSubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [status, setStatus] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);

  // See the header comment: these survive close, unlike everything above.
  const inFlightRef = useRef(false);
  const lockedRef = useRef(false);
  /** Set once an exclusion is CONFIRMED, so reopening cannot exclude the same case twice. */
  const succeededRef = useRef(false);
  const sessionRef = useRef(0);
  // Remounts ExcludeModal fresh on every open, so no stale render can be seen between close and the
  // effect-based reset committing.
  const [openGen, setOpenGen] = useState(0);
  const caseKeyRef = useRef(caseKey);

  // Bumping `sessionRef` invalidates any outstanding request so its settle path cannot write the old
  // case's outcome into the new one.
  useEffect(() => {
    if (caseKeyRef.current === caseKey) return;
    caseKeyRef.current = caseKey;
    sessionRef.current += 1;
    inFlightRef.current = false;
    lockedRef.current = false;
    succeededRef.current = false;
    setIsOpen(false);
    setSubmitState('idle');
    setErrorMessage(undefined);
    setStatus('');
  }, [caseKey]);

  const open = (): void => {
    setLoggedAs({ name: nameOf(data), at: new Date() }); // frozen per open; never read during render
    setErrorMessage(undefined);
    setStatus('');
    if (succeededRef.current) {
      // Already excluded in this session. Show the terminal state rather than a clean form.
      setSubmitState('error');
      setErrorMessage('This case has already been excluded.');
    } else if (lockedRef.current) {
      // A previous ambiguous failure keeps its error visible on reopen rather than presenting a clean form
      // the user could resubmit -- the copy tells them to reload, and the form must not contradict it.
      setSubmitState('error');
      setErrorMessage(
        "Couldn't confirm the exclusion. Reload the case before retrying."
      );
    } else if (inFlightRef.current) {
      // Reopened while the FIRST request is still outstanding. Present it as busy, not idle: an idle,
      // fully-enabled form whose submit button silently does nothing is worse than a disabled one.
      setSubmitState('excluding');
    } else {
      setSubmitState('idle');
    }
    sessionRef.current += 1;
    setOpenGen((g) => g + 1);
    setIsOpen(true);
  };

  const close = (): void => {
    setIsOpen(false);
    if (!lockedRef.current && !succeededRef.current) {
      setSubmitState('idle');
      setErrorMessage(undefined);
    }
  };

  const onSubmit = async (payload: ExcludePayload): Promise<void> => {
    setErrorMessage(undefined);
    // Guards the MEMBER, not the object: calling a missing member throws a TypeError the catch below
    // would misreport as a failed exclusion.
    if (typeof api?.excludeCase !== 'function') {
      setSubmitState('error');
      setErrorMessage("Case exclusion isn't available yet.");
      return;
    }
    if (succeededRef.current) {
      setSubmitState('error');
      setErrorMessage('This case has already been excluded.');
      return;
    }
    if (lockedRef.current) {
      setSubmitState('error');
      setErrorMessage(
        "Couldn't confirm the exclusion. Reload the case before retrying."
      );
      return;
    }
    // No-op, not an error: the honest state is still "excluding" and the first request owns the outcome.
    if (inFlightRef.current) return;

    const submittedCase = caseKeyRef.current;
    inFlightRef.current = true;
    setSubmitState('excluding');
    setStatus('Excluding case…');
    try {
      await api.excludeCase(payload);
    } catch {
      inFlightRef.current = false;
      if (submittedCase !== caseKeyRef.current) return; // outcome belongs to a case the user has left
      // Locked whichever modal session is current: the write may have landed, so no reopen may retry.
      lockedRef.current = true;
      // NOT gated on the session matching: reopening mid-flight renders the new session busy on this
      // request's behalf, so returning without reconciling would strand it there.
      setSubmitState('error');
      setStatus('');
      setErrorMessage(
        "Couldn't confirm the exclusion. Reload the case before retrying."
      );
      return;
    }
    inFlightRef.current = false;
    if (submittedCase !== caseKeyRef.current) return; // outcome belongs to a case the user has left
    succeededRef.current = true;
    // Announced and closed even if the user reopened mid-flight — see the rejection path above.
    setStatus('Case excluded and archived.');
    close();
  };

  // Explicit denial always wins, whatever else is true.
  if (data.canExclude === false) return null;
  // Capability-aware: permissive while `excludeCase` is unimplemented (the action is inert), an
  // allow-list once a write exists — so wiring a write without entitlement hides the control rather
  // than shipping it ungated.
  if (typeof api?.excludeCase === 'function' && data.canExclude !== true) {
    return null;
  }

  return (
    <>
      <Tooltip label={EXCLUDE_MODAL_TITLE} placement="bottom" openDelay={300}>
        <Button
          ref={triggerRef}
          data-testid="exclude-icon"
          aria-label={EXCLUDE_MODAL_TITLE}
          onClick={open}
          variant="unstyled"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          minW="auto"
          h="auto"
          p={0}
        >
          <Box
            aria-hidden
            bg={EXCLUDE_COLORS.iconBg}
            color={EXCLUDE_COLORS.iconStroke}
            borderRadius={CASE_ACTION_RADIUS.control}
            w={EXCLUDE_SIZES.triggerTile}
            h={EXCLUDE_SIZES.triggerTile}
            flexShrink={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <ExcludeGlyph size={EXCLUDE_GLYPH_PX.trigger} />
          </Box>
        </Button>
      </Tooltip>
      {/* Lives OUTSIDE the modal so a success announcement survives the modal unmounting. Without it,
          success would be entirely silent while failure was announced -- an announcement channel for the
          harmless outcome and none for the irreversible one. */}
      <Box
        data-testid="exclude-status"
        role="status"
        aria-live="polite"
        position="absolute"
        width="1px"
        height="1px"
        overflow="hidden"
        clipPath="inset(50%)"
        whiteSpace="nowrap"
      >
        {status}
      </Box>
      {loggedAs && (
        <ExcludeModal
          key={openGen}
          isOpen={isOpen}
          onClose={close}
          loggedAs={loggedAs}
          onSubmit={onSubmit}
          submitState={submitState}
          errorMessage={errorMessage}
          reasonOptions={asOptions(data.reasonOptions)}
          finalFocusRef={triggerRef}
        />
      )}
    </>
  );
};
